/** Group keys/labels from server catalog (`lib/permissions/catalog.yml`). */
export const PERMISSION_GROUPS = [
  { key: "admin_management", label: "Admin Management" },
  { key: "roles_management", label: "Roles & Permissions" },
  { key: "assessment_management", label: "Assessment Management" },
  { key: "organizations_management", label: "Organizations" },
  { key: "users_management", label: "Users" },
];

export const PERMISSION_GROUP_LABELS = Object.fromEntries(
  PERMISSION_GROUPS.map((group) => [group.key, group.label])
);

/**
 * Group permissions by catalog `group`, preserving catalog section order.
 * @param {Array<{ group?: string|null, name?: string }>} permissions
 * @returns {Array<{ key: string, label: string, permissions: typeof permissions }>}
 */
export function groupPermissions(permissions = []) {
  const byGroup = new Map();

  permissions.forEach((permission) => {
    const key = permission.group || "other";
    if (!byGroup.has(key)) byGroup.set(key, []);
    byGroup.get(key).push(permission);
  });

  const orderedKeys = [
    ...PERMISSION_GROUPS.map((group) => group.key).filter((key) =>
      byGroup.has(key)
    ),
    ...[...byGroup.keys()].filter(
      (key) => !PERMISSION_GROUP_LABELS[key] && key !== "other"
    ),
    ...(byGroup.has("other") ? ["other"] : []),
  ];

  return orderedKeys.map((key) => ({
    key,
    label: PERMISSION_GROUP_LABELS[key] || titleizeGroup(key),
    permissions: byGroup.get(key) || [],
  }));
}

function titleizeGroup(key) {
  return key
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

/** Permission keys from server catalog (`lib/permissions/catalog.yml`). */
export const PERMISSIONS = {
  // Admin management
  ADMINS_CREATE: "admins_create",
  ADMINS_EDIT: "admins_edit",
  ADMINS_ACTIVATE: "admins_activate",
  ADMINS_INACTIVATE: "admins_inactivate",
  ADMINS_DELETE: "admins_delete",
  ADMINS_ASSIGN_MASTER: "admins_assign_master",
  ADMINS_MANAGE_OWN_PERMISSIONS: "admins_manage_own_permissions",
  ADMINS_MANAGE_OTHER_PERMISSIONS: "admins_manage_other_permissions",

  // Roles management
  ROLES_VIEW: "roles_view",
  ROLES_CREATE: "roles_create",
  ROLES_EDIT: "roles_edit",
  ROLES_DEACTIVATE: "roles_deactivate",
  ROLES_REACTIVATE: "roles_reactivate",
  ROLE_PERMISSIONS_MANAGE: "role_permissions_manage",
  ROLES_ASSIGN: "roles_assign",

  // Assessment management
  ASSESSMENT_INSTRUMENTS_VIEW: "assessment_instruments_view",
  ASSESSMENT_INSTRUMENTS_CREATE: "assessment_instruments_create",
  ASSESSMENT_INSTRUMENTS_EDIT: "assessment_instruments_edit",
  ASSESSMENT_INSTRUMENTS_DELETE: "assessment_instruments_delete",
  CONCEPTUAL_ROOTS_VIEW: "conceptual_roots_view",
  CONCEPTUAL_ROOTS_CREATE: "conceptual_roots_create",
  CONCEPTUAL_ROOTS_EDIT: "conceptual_roots_edit",
  CONCEPTUAL_ITEMS_CREATE: "conceptual_items_create",
  CONCEPTUAL_ITEMS_EDIT: "conceptual_items_edit",
  ORG_ASSESSMENT_INSTRUMENTS_VIEW: "org_assessment_instruments_view",
  ORG_ASSESSMENT_INSTRUMENTS_MANAGE: "org_assessment_instruments_manage",
  OCCUPATIONAL_PROFILES_VIEW: "occupational_profiles_view",
  OCCUPATIONAL_PROFILES_CREATE: "occupational_profiles_create",
  OCCUPATIONAL_PROFILES_EDIT: "occupational_profiles_edit",
  OCCUPATIONAL_PROFILES_DELETE: "occupational_profiles_delete",
  ORG_OCCUPATIONAL_PROFILES_VIEW: "org_occupational_profiles_view",
  ORG_OCCUPATIONAL_PROFILES_MANAGE: "org_occupational_profiles_manage",
  ASSESSMENTS_APPROVE: "assessments_approve",
  ASSESSMENTS_EVALUATE: "assessments_evaluate",
  ASSESSMENTS_VIEW: "assessments_view",
  ORG_FINALIZATION_PERMITTED: "org_finalization_permitted",
  DIRECT_ASSIGNMENT_PERMITTED: "direct_assignment_permitted",
  ASSESSMENTS_REQUEST: "assessments_request",
  ASSESSMENTS_REQUEST_TO_PLATFORM: "assessments_request_to_platform",
  ASSESSMENTS_ORG_APPROVE: "assessments_org_approve",
  ASSESSMENTS_ASSIGN: "assessments_assign",

  // Organizations management
  ORGANIZATIONS_VIEW: "organizations_view",
  ORGANIZATIONS_CREATE: "organizations_create",
  ORGANIZATIONS_EDIT: "organizations_edit",
  ORGANIZATIONS_DELETE: "organizations_delete",
  ORGANIZATION_ADMINS_VIEW: "organization_admins_view",
  ORGANIZATION_ADMINS_MANAGE: "organization_admins_manage",
  ORGANIZATION_MANAGERS_VIEW: "organization_managers_view",
  ORGANIZATION_MANAGERS_MANAGE: "organization_managers_manage",
  ORGANIZATION_USERS_VIEW: "organization_users_view",
  ORGANIZATION_USERS_MANAGE: "organization_users_manage",
  ORGANIZATION_MANAGED_USERS_VIEW: "organization_managed_users_view",
  ORGANIZATION_MANAGED_USERS_MANAGE: "organization_managed_users_manage",

  // Users management
  USERS_VIEW: "users_view",
  USERS_CREATE: "users_create",
  USERS_EDIT: "users_edit",
  USERS_ACTIVATE: "users_activate",
  USERS_INACTIVATE: "users_inactivate",
  USERS_DELETE: "users_delete",

  CHAT_USE: "chat_use",
};

/** Catalog role names from server `lib/permissions/catalog.yml`. */
export const CATALOG_ROLE_NAMES = {
  MASTER_ADMIN: "master admin",
  ORG_ADMIN: "org admin",
  ORG_MANAGER: "org manager",
};

/**
 * Org-staff catalog roles are assigned to organization users, not platform admins.
 * @param {{ name?: string }|null|undefined} role
 */
export function isOrgStaffCatalogRole(role) {
  const name = (role?.name || "").toLowerCase().trim();
  return (
    name === CATALOG_ROLE_NAMES.ORG_ADMIN ||
    name === CATALOG_ROLE_NAMES.ORG_MANAGER
  );
}

/**
 * @param {string[]|null|undefined} granted
 * @param {string|string[]} required
 * @param {{ mode?: "all" | "any" }} [options]
 */
export function hasPermission(granted, required, options = {}) {
  const { mode = "all" } = options;
  const keys = Array.isArray(required) ? required : [required];
  if (!keys.length) return true;

  const set = new Set(granted || []);
  if (mode === "any") {
    return keys.some((key) => set.has(key));
  }
  return keys.every((key) => set.has(key));
}
